import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Grid,
  MenuItem,
  Radio,
  FormControlLabel,
  InputLabel,
  Button
} from "@mui/material";
import { TextField, Select, RadioGroup } from "formik-mui";
import { Field } from "formik";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import UploadButton from "../../../../components/Buttons/UploadButton";
import { PropTypes } from "prop-types";
import UneditableField from "../../../../components/NonEditableField/NonEditableField";
import { courseTypes } from "../../../../constants";
import usePermissions from "../../../../redux/selectors/usePermissions";
import WarningModal from "../../../../components/WarningModal/WarningModal";
import { assignmentReferenceTypes } from "../../../../constants";
import { groupBy } from "underscore";
import { FileCopy } from "@mui/icons-material"; //TODO: Update material ui to have acces to larger selection of icons
import { enqueueSuccessSnackbar } from "../../../../redux/actions/snackbarActions";
import ChipInput from "../../../../components/FormikFields/ChipInput";
import { createDeepLinkString } from "../../../../utils/deepLinking";
import { useDispatch, connect, useSelector } from "react-redux";

const minuteDurationSet = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45];
const descriptionMaxCharacters = 800;
const suggestedImageDimensions = "480 x 480";

const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: "bold",
    minWidth: 150
  },
  linkContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center"
  },
  linkTypography: {
    marginLeft: 4,
    display: "flex",
    alignItems: "center"
  },
  linkIcon: {
    marginLeft: 4,
    cursor: "pointer"
  },
  assignments: {
    cursor: "pointer"
  }
}));

const InactivateCourseForbiddenMessage = ({
  assignments,
  enrolledAssignments
}) => {
  const { t } = useTranslation();

  let assignmentGroups = groupBy(assignments, obj => obj.referenceType);

  if (enrolledAssignments && enrolledAssignments.length > 0) {
    const enrolled = enrolledAssignments.map(obj => ({
      id: `ua${obj.id}`,
      name: `${obj.firstName} ${obj.lastName}`
    }));
    if (assignmentGroups["Individual"]) {
      assignmentGroups["Individual"] = assignmentGroups["Individual"].concat(
        enrolled
      );
    } else {
      assignmentGroups["Individual"] = enrolled;
    }
  }

  return (
    <Box>
      <Typography>
        {t("manageCourses.inactivateCourseForbiddenMessage")}
      </Typography>
      {assignmentReferenceTypes.map(type => {
        if (assignmentGroups[type.value]) {
          return (
            <Box key={type.value}>
              <br />
              <Typography>{t(type.label)}</Typography>
              {assignmentGroups[type.value].map(obj => (
                <Box display="flex" ml={2} key={obj.id}>
                  <Typography>- {obj.name}</Typography>
                </Box>
              ))}
            </Box>
          );
        }
        return <React.Fragment key={Math.random()}></React.Fragment>;
      })}
    </Box>
  );
};

const InactivateCourseNoticeModal = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <WarningModal
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      showCloseIcon={false}
      warningMessage={t("manageCourses.inactivateCourseWarningMessage")}
    />
  );
};

const DisplayInLibraryWarningModal = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <WarningModal
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      showCloseIcon={false}
      warningMessage={t("manageCourses.displayInLibraryWarningMessage")}
    />
  );
};

const CourseSettingsGlobal = ({
  values,
  setFieldValue,
  isDisabled,
  customContent,
  masterCourse,
  courseType,
  assignments,
  enrolledAssignments,
  submitForm,
  displayInLibraryInitialLabel,
  displayInLibraryWarningModalOpen,
  setDisplayInLibraryWarningModalOpen,
  id
}) => {
  const classes = useStyles();
  const [imagePreview, setImagePreview] = useState("");
  const [imgError, setImgError] = useState("");
  const [assignmentsNoticeModalOpen, setAssignmentsNoticeModalOpen] = useState(
    false
  );
  const [
    inactivateCourseWarningModalOpen,
    setInactivateCourseWarningModalOpen
  ] = useState(false);
  const { t } = useTranslation();
  const { allowShopifyStorefront, allowDeepLinking } = usePermissions();
  const disabled = isDisabled();
  const dispatch = useDispatch();
  const customerId = useSelector(state => state.customerState.customer.id);

  const copyLink = async () => {
    const deepLinkString = await createDeepLinkString(customerId, id);
    try {
      const permission = await navigator.permissions.query({
        name: "clipboard-write"
      });
      if (permission.state == "granted" || permission.state == "prompt") {
        navigator.clipboard.writeText(deepLinkString);
        dispatch(
          enqueueSuccessSnackbar({
            message: t("manageCourses.linkCopySuccessMessage")
          })
        );
      }
    } catch {
      navigator.clipboard.writeText(deepLinkString);
      dispatch(
        enqueueSuccessSnackbar({
          message: t("manageCourses.linkCopySuccessMessage")
        })
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" mt={4}>
      <WarningModal
        open={assignmentsNoticeModalOpen}
        onClose={() => setAssignmentsNoticeModalOpen(false)}
        onSubmit={() => setAssignmentsNoticeModalOpen(false)}
        showCloseIcon={false}
        warningMessage={
          <InactivateCourseForbiddenMessage
            assignments={assignments}
            enrolledAssignments={enrolledAssignments}
          />
        }
      />
      <InactivateCourseNoticeModal
        open={inactivateCourseWarningModalOpen}
        onClose={() => {
          setFieldValue("isActive", "yes");
          setInactivateCourseWarningModalOpen(false);
        }}
        onSubmit={() => {
          setInactivateCourseWarningModalOpen(false);
          submitForm();
        }}
      />
      <DisplayInLibraryWarningModal
        open={displayInLibraryWarningModalOpen}
        onClose={() => {
          setFieldValue("displayInLibrary", displayInLibraryInitialLabel);
          setDisplayInLibraryWarningModalOpen(false);
        }}
        onSubmit={() => {
          setDisplayInLibraryWarningModalOpen(false);
          submitForm();
        }}
      />
      <Box display="flex" mb={3}>
        <Typography variant="h5">
          {t("manageCourses.courseSettingsGlobalSettingsHeader")}
        </Typography>
      </Box>
      <Grid container spacing={4} sx={{ alignItems: "center" }}>
        <Grid item xs={12}>
          {customContent || masterCourse ? (
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.courseSettingsCourseTitleField")}
              component={TextField}
              name="courseTitle"
            />
          ) : (
            <UneditableField
              fieldName={t("manageCourses.courseSettingsCourseTitleField")}
              fieldValue={values.courseTitle}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          {customContent || masterCourse ? (
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.courseSettingsDescriptionField")}
              component={TextField}
              name="description"
              multiline
              helperText={`${descriptionMaxCharacters -
                values.description.length}/${descriptionMaxCharacters} ${t(
                "charactersRemaining"
              )}`}
              rows={8}
            />
          ) : (
            <UneditableField
              fieldName={t("manageCourses.courseSettingsDescriptionField")}
              fieldValue={values.description}
            />
          )}
        </Grid>
        {(customContent || masterCourse) && (
          <Grid item xs={12}>
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.courseSettingsTagsField")}
              component={ChipInput}
              name="tags"
              inputProps={{
                "data-qa": "course-settings-global-tags-field"
              }}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          {customContent || masterCourse ? (
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.courseSettingsDurationHoursField")}
              component={TextField}
              name="durationHours"
              type="number"
              inputProps={{ min: 0 }}
            />
          ) : (
            <UneditableField
              fieldName={t("manageCourses.courseSettingsDurationHoursField")}
              fieldValue={values.durationHours}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {customContent || masterCourse ? (
            <FormControl fullWidth>
              <InputLabel shrink={true} htmlFor="durationMinutes">
                {t("manageCourses.courseSettingsDurationMinutesField")}
              </InputLabel>
              <Field
                type="text"
                name="durationMinutes"
                component={Select}
                inputProps={{ name: "durationMinutes", id: "durationMinutes" }}
                variant="filled"
                fullWidth
                renderValue={value => `${value} ${t("minutesAbbreviated")}`}
              >
                {minuteDurationSet.map((item, ind) => (
                  <MenuItem value={item} key={ind}>
                    {item}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
          ) : (
            <UneditableField
              fieldName={t("manageCourses.courseSettingsDurationMinutesField")}
              fieldValue={values.durationMinutes}
            />
          )}
        </Grid>

        {(customContent || masterCourse) && (
          <>
            <Grid item xs={6}>
              <Typography variant="subtitle2" className={classes.label}>
                {t("manageCourses.courseSettingsIncludesCertificateField")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Field
                  type="text"
                  variant="filled"
                  component={RadioGroup}
                  name="includeCertificate"
                  row
                >
                  <FormControlLabel
                    disabled={masterCourse}
                    value="yes"
                    control={<Radio color="primary" />}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    disabled={masterCourse}
                    value="no"
                    control={<Radio color="primary" />}
                    label={t("no")}
                  />
                </Field>
              </FormControl>
            </Grid>
          </>
        )}
        {courseType !== courseTypes.ilt && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.label}>
                {t("manageCourses.courseSettingsDisplayInLibraryField")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                data-qa="course-settings-display-in-library"
              >
                <Field
                  type="text"
                  variant="filled"
                  component={RadioGroup}
                  name="displayInLibrary"
                  row
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="primary" />}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="primary" />}
                    label={t("no")}
                  />
                </Field>
              </FormControl>
            </Grid>
          </>
        )}

        {allowShopifyStorefront && courseType !== courseTypes.ilt && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.label}>
                {t("manageCourses.courseSettingsDisplayInStorefrontField")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Field
                  type="text"
                  variant="filled"
                  component={RadioGroup}
                  name="displayInStorefront"
                  row
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="primary" />}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="primary" />}
                    label={t("no")}
                  />
                </Field>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.label}>
                {t("manageCourses.courseSettingsIsCredentialField")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Field
                  type="text"
                  variant="filled"
                  component={RadioGroup}
                  name="isCredential"
                  row
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="primary" />}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="primary" />}
                    label={t("no")}
                  />
                </Field>
              </FormControl>
            </Grid>
          </>
        )}

        {customContent && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.label}>
                {t("manageCourses.courseSettingsIsActiveField")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Field
                  type="text"
                  variant="filled"
                  component={RadioGroup}
                  name="isActive"
                  row
                >
                  <FormControlLabel
                    disabled={disabled}
                    value="yes"
                    control={<Radio color="primary" />}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    disabled={disabled}
                    value="no"
                    control={<Radio color="primary" />}
                    label={t("no")}
                    onClick={() =>
                      !disabled && setInactivateCourseWarningModalOpen(true)
                    }
                  />
                </Field>
              </FormControl>
              {customContent && disabled && (
                <Typography variant="body2" color="textSecondary">
                  {t("manageCourses.activeAssignmentsNotice")}
                  <u
                    onClick={() => setAssignmentsNoticeModalOpen(true)}
                    className={classes.assignments}
                  >
                    {t("manageCourses.assignments")}
                  </u>
                </Typography>
              )}
            </Grid>
          </>
        )}

        {allowDeepLinking && (
          <Grid
            container
            item
            spacing={4}
            sx={{ alignItems: "center", minWidth: { xs: 0, md: 500 } }}
          >
            <Grid item>
              <Box className={classes.linkContainer}>
                <Typography variant="body2" className={classes.label}>
                  {t("manageCourses.deepLink")}
                </Typography>
                <Box className={classes.linkTypography}>
                  <Button color="primary" onClick={copyLink}>
                    {t("manageCourses.copyLink")}
                    <FileCopy className={classes.linkIcon} />
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {(customContent || masterCourse) && (
          <Grid
            container
            item
            spacing={3}
            sx={{ alignItems: "center", minWidth: { xs: 0, md: 500 } }}
          >
            <Grid item xs={12} sm={6} sx={{ minWidth: 200 }}>
              <img
                height="200px"
                width="200px"
                alt="Course"
                src={imagePreview ? imagePreview : values.courseImage}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Box ml={-2} mb={1}>
                  <UploadButton
                    acceptType="*"
                    buttonVariant="text"
                    buttonText="Upload Image"
                    buttonId="courseImage"
                    onUpload={e => {
                      const file = e.target.files[0];
                      const url = URL.createObjectURL(file);
                      const img = new Image();
                      img.onload = () => {
                        if (
                          img.width !== img.height ||
                          img.width < 480 ||
                          img.height < 480
                        ) {
                          setImgError(
                            t("manageCourses.courseSettingsImageError")
                          );
                          setImagePreview("");
                          setFieldValue("courseImage", null);
                          return;
                        }

                        setImgError("");
                        setImagePreview(url);
                        setFieldValue("courseImage", file);
                      };
                      img.src = url;
                    }}
                    multiple={false}
                  />
                </Box>
                {!imgError && (
                  <Typography variant="body2" color="textSecondary">
                    {t("manageCourses.courseSettingsImageSize", {
                      imageDimensions: suggestedImageDimensions
                    })}
                  </Typography>
                )}
                {imgError && (
                  <Typography variant="body2" color="error">
                    {imgError}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const mapStateToProps = state => ({
  customer: state.customerState.customer.id
});

export default connect(mapStateToProps)(CourseSettingsGlobal);

CourseSettingsGlobal.propTypes = {
  values: PropTypes.shape({
    courseTitle: PropTypes.string,
    description: PropTypes.string,
    tags: PropTypes.array,
    durationHours: PropTypes.number,
    durationMinutes: PropTypes.number,
    includeCertificate: PropTypes.string,
    displayInLibrary: PropTypes.string,
    hideFromLibrary: PropTypes.string,
    isActive: PropTypes.string,
    timeToComplete: PropTypes.any,
    timeToCompleteUnit: PropTypes.string,
    recordScore: PropTypes.string,
    passingScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recurringEvent: PropTypes.string,
    recurringInterval: PropTypes.number,
    recurringIntervalUnit: PropTypes.string,
    courseImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  }),
  setFieldValue: PropTypes.func,
  isDisabled: PropTypes.func
};
